import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const API_BASE = "https://api-2026.fikacreatives.co";
const BOOTSTRAP_PATH = "/api/me/bootstrap";

const AuthBootstrapContext = createContext(null);

async function readJsonSafely(res) {
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) return await res.json();

  const text = await res.text();
  return {
    ok: false,
    error: `Expected JSON but got ${ct || "unknown"}: ${text.slice(0, 120)}`,
  };
}

export function AuthBootstrapProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [authed, setAuthed] = useState(false);
  const [accessGranted, setAccessGranted] = useState(false);
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [onboardingRequired, setOnboardingRequired] = useState(false);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  const [person, setPerson] = useState(null);
  const [profileAssets, setProfileAssets] = useState(null);
  const [verification, setVerification] = useState(null);
  const [error, setError] = useState("");

  const resetAuthState = useCallback(() => {
    setAuthed(false);
    setAccessGranted(false);
    setVerificationRequired(false);
    setOnboardingRequired(false);
    setOnboardingCompleted(false);
    setPerson(null);
    setProfileAssets(null);
    setVerification(null);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${API_BASE}${BOOTSTRAP_PATH}`, {
        method: "GET",
        credentials: "include",
        cache: "no-store",
        headers: {
          Accept: "application/json",
        },
      });

      const data = await readJsonSafely(res);

      if (!res.ok || !data?.ok) {
        resetAuthState();
        setError(data?.error || "Could not load account.");
        return;
      }

      setAuthed(Boolean(data?.authed));
      setAccessGranted(Boolean(data?.accessGranted));
      setVerificationRequired(Boolean(data?.verificationRequired));
      setOnboardingRequired(Boolean(data?.onboardingRequired));
      setOnboardingCompleted(Boolean(data?.onboardingCompleted));
      setPerson(data?.person || null);
      setProfileAssets(data?.profileAssets || null);
      setVerification(data?.verification || null);
    } catch (err) {
      console.error("AuthBootstrap refresh error:", err);
      resetAuthState();
      setError("Network error while loading account.");
    } finally {
      setLoading(false);
    }
  }, [resetAuthState]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({
      loading,
      authed,
      accessGranted,
      verificationRequired,
      onboardingRequired,
      onboardingCompleted,
      person,
      profileAssets,
      verification,
      error,
      refresh,
      setProfileAssets,
    }),
    [
      loading,
      authed,
      accessGranted,
      verificationRequired,
      onboardingRequired,
      onboardingCompleted,
      person,
      profileAssets,
      verification,
      error,
      refresh,
    ]
  );

  return (
    <AuthBootstrapContext.Provider value={value}>
      {children}
    </AuthBootstrapContext.Provider>
  );
}

export function useAuthBootstrap() {
  const ctx = useContext(AuthBootstrapContext);

  if (!ctx) {
    throw new Error("useAuthBootstrap must be used within AuthBootstrapProvider");
  }

  return ctx;
}